// @flow
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  LocalOffer as ApplyIcon,
  OpenInNew,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignProcess from '../model/SignProcess';
import type { PaymentProps } from '../model/props';
import { PaymentTypes } from '../model/props';
import type { BankData } from '../model/TransferData';
import { Skeleton } from '@material-ui/lab';
import PaymentData from '../model/PaymentData';

const maxSize: number = Number.parseInt(`${process.env.REACT_APP_UPLOAD_LIMIT || '0'}`);

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary['900'],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  upload: {
    display: 'none',
  },
}));

type Props = {
  code: string,
  process: SignProcess,
  expanded: boolean,
  loading: boolean,
  disabled: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: (payment: PaymentProps) => Promise<boolean>,
  onApplyCoupon: (coupon: string) => Promise<boolean>,
};

const ProcessPaymentData = (props: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { code, process, expanded, loading, disabled: propsDisabled, onExpanded, onSave, onApplyCoupon } = props;
  const { transferData, hasPayValidated, couponDescription } = process;

  const [mode, setMode] = useState < string > (process.paymentType || process.paymentTypes[0]);
  const [coupon, setCoupon] = useState <? string > (process.coupon);
  const [file, setFile] = useState < any > ();
  const [paymentLoading, setPaymentLoading] = useState < boolean > (false);
  const [paymentData, setPaymentData] = useState <? PaymentData > ();

  const disabled = propsDisabled || hasPayValidated;

  const loadPayment = () => {
    if (!disabled && mode.toLowerCase() === PaymentTypes.CREDIT_CARD.toLowerCase()
      && (!paymentData || !!paymentData.error)) {
      setPaymentLoading(true);
      process.getPaymentData()
        .then(data => {
          setPaymentData(data);
          setPaymentLoading(false);
        });
    }
  };

  const applyCoupon = () => {
    if (!disabled && !!coupon) {
      onApplyCoupon(coupon);
    }
  };

  useEffect(loadPayment, [mode]);

  useEffect(() => {
    if (!!paymentData && !!paymentData.scripts) {
      const scripts = paymentData.scripts.map(url => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        if (!!document && !!document.body) {
          document.body.appendChild(script);
        }
        return script;
      });
      return () => {
        for (let script of scripts) {
          if (!!document && !!document.body) {
            document.body.removeChild(script);
          }
        }
      }
    }
  }, [paymentData]);

  let processStatusText = process.hasPaymentLoaded() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasPaymentLoaded() ? <DoneIcon color="action" /> : <EditIcon color="secondary" />;
  if (process.hasPaymentValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon color="primary" />;
  }

  const onUpload = () => {
    onSave({
      file,
      mode,
      coupon
    }).then((ok: boolean) => {
      if (ok) {
        setFile(null);
      }
    });
  };

  const isTransfer = mode.toLowerCase() === PaymentTypes.TRANSFER.toLowerCase();
  const isCredit = mode.toLowerCase() === PaymentTypes.CREDIT.toLowerCase();
  const isCreditCard = mode.toLowerCase() === PaymentTypes.CREDIT_CARD.toLowerCase();

  return (
    <Accordion
      key={code}
      expanded={expanded}
      onChange={onExpanded(code)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
      >
        {processStatusIcon}&nbsp;
        <Typography className={classes.heading}>
          {t('Payment data')}
        </Typography>
        <Typography className={classes.secondaryHeading}>
          {t(processStatusText)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <FormControl
              fullWidth
              variant="outlined"
              disabled={disabled || paymentLoading}
            >
              <InputLabel id="paymentTypeLabel">
                {t('Payment type')}
              </InputLabel>
              <Select
                labelId="paymentTypeLabel"
                label={t('Payment type')}
                id="paymentType"
                name="paymentType"
                value={mode}
                onChange={(e) => {
                  if (e && e.target) {
                    setMode(e.target.value);
                  }
                }}
              >
                {process.paymentTypes.map((item) => (
                  <MenuItem key={item} value={item}>
                    {t(item)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <TextField
              disabled={disabled || paymentLoading}
              autoComplete="coupon"
              name="coupon"
              variant="outlined"
              id="coupon"
              label={t('Coupon')}
              onChange={(e) => {
                if (e && e.target) {
                  setCoupon(e.target.value);
                }
              }}
              value={coupon}
            />
            <Button
              variant="contained"
              color="secondary"
              aria-label="apply"
              onClick={applyCoupon}
              startIcon={<ApplyIcon />}
              disabled={disabled || !coupon || coupon.length < 6 || coupon === process.coupon || paymentLoading}
              style={{ marginLeft: 10 }}
            >
              {t('Apply coupon')}
            </Button>
          </Grid>
          {!!couponDescription && <Grid item xs={12} sm={12} md={12} lg={12}>
            <Typography className={classes.secondaryHeading}>
              <b>{t('Applied coupon')}: {couponDescription}</b>
            </Typography>
            <Typography variant='subtitle2' className={classes.grey}>
              <b>{t('Total price')}: ${process.price.toFixed(2)}.</b>
            </Typography>
          </Grid>}
          {!!hasPayValidated && <Grid item xs={12} sm={12} md={12} lg={12}>
            <Typography className={classes.secondaryHeading}>
              {t('Payment validated')}
            </Typography>
          </Grid>}
          {isTransfer && !!transferData && <Grid item xs={12} sm={12} md={12} lg={12}>
            <Typography className={classes.secondaryHeading}>
              {t('Transfer description')}
            </Typography>
            {transferData.banks.map((bank: BankData) => (
              <Typography key={bank.code} className={classes.secondaryHeading}>
                <b>{bank.name}</b>: {bank.code}
              </Typography>
            ))}
            <Typography className={classes.secondaryHeading}>
              {t('target to')}: <b>{transferData.target}</b>
            </Typography>
            <Typography className={classes.secondaryHeading}>
              {t('Legal ID')}: <b>{transferData.legalID}</b>
            </Typography>
            <Typography className={classes.secondaryHeading}>
              {t('Email')}: <b>{transferData.email}</b>
            </Typography>
            <Typography className={classes.secondaryHeading}>
              {t('Phone')}: <b>{transferData.phone}</b>
            </Typography>
            <Typography className={classes.secondaryHeading}>
              {t('Address')}: <b>{transferData.address}</b>
            </Typography>
          </Grid>}
          {isTransfer && <Grid item xs={12} sm={12} md={12} lg={12}>
            <input
              accept=".pdf,.png,.jpg"
              className={classes.upload}
              id="proof-of-payment"
              type="file"
              onChange={(e) => {
                if (e.target.files
                  && e.target.files.length) {
                  const file = e.target.files[0];
                  if (file.size >= maxSize) {
                    enqueueSnackbar(t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }), {
                      variant: 'error',
                    });
                  } else {
                    setFile(file);
                  }
                }
              }}
            />
            <label htmlFor="proof-of-payment">
              {!file && <Button
                disabled={disabled || paymentLoading}
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                component="span">
                {t(`Upload proof of payment`)}
              </Button>}
              {file && <span>
                <Typography variant="subtitle2" className={classes.grey}>
                  {t('Proof of payment')}&nbsp;
                  <Button
                    disabled={disabled || paymentLoading}
                    variant="contained"
                    color="primary"
                    startIcon={loading ? <CircularProgress color="inherit" size="1rem" /> : <UploadIcon />}
                    aria-label="upload"
                    onClick={onUpload}
                  >
                    {t('Upload')}
                  </Button>
                  <IconButton
                    disabled={disabled || paymentLoading}
                    aria-label="delete"
                    className={classes.error}
                    onClick={() => {
                      setFile(null);
                    }}>
                    <DeleteIcon />
                  </IconButton>
                </Typography>
              </span>}
            </label>
          </Grid>}
          {isCreditCard && <Grid item xs={12} sm={12} md={12} lg={12}>
            {paymentLoading && [1, 2, 3, 4].map((index) => (
              <Skeleton key={index} />
            ))}
            {(!!paymentData && !paymentLoading && !!paymentData.error) && <>
              <IconButton aria-label="reload" onClick={loadPayment}>
                <RefreshIcon className={classes.error} />
              </IconButton>
              <span className={classes.error}>
                {t(paymentData.error)}
              </span>
            </>}
            {(!paymentData && !paymentLoading && !disabled) && <>
              <Button
                onClick={loadPayment}
                disabled={disabled || paymentLoading}
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
              >
                {t('Load link')}
              </Button>
            </>}
            {(!!paymentData && !paymentLoading && !paymentData.error && !!paymentData.scripts)
              && <form action={paymentData.callbackUrl}
                className="paymentWidgets"
                data-brands="AMEX DINERS DISCOVER MAESTRO MASTER VISA">
              </form>
            }
            {(!!paymentData && !paymentLoading && !paymentData.error && !!paymentData.linkData)
              && <a href={paymentData.linkData.url} target="_blank" rel="noopener noreferrer">
                {t('Go to payment')}&nbsp;<OpenInNew />
              </a>
            }
          </Grid>}
        </Grid>
      </AccordionDetails>
      {isCredit && <Divider />}
      {isCredit && <AccordionActions>
        <Button
          disabled={disabled || paymentLoading}
          variant="contained"
          color="primary"
          className={classes.button}
          aria-label="save"
          onClick={onUpload}>
          {t(`Save`)}
        </Button>
      </AccordionActions>}
    </Accordion>
  );
};

export default ProcessPaymentData;
